import { Typography } from "@mui/material";
import { createContext, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Case } from "../../shared/CaseModel";
import { judgmentOf } from "../../shared/judgment";
import { judgedRowsOf } from "../../shared/judgmentDecisions";

/**
 * The case of a judgment in force, for the claims page: without one, as
 * without this context, its rows are those of a case still open.
 */
export const JudgedCaseContext = createContext<Case | undefined>(undefined);

/** What the judgment in force decided of the claims, if there is one. */
export const useJudgedRows = () => {
  const c = useContext(JudgedCaseContext);
  return c && judgedRowsOf(c);
};

/** The judgment in force on the claims page, if there is one. */
export const useJudgment = () => {
  const c = useContext(JudgedCaseContext);
  return c && judgmentOf(c);
};

/** What became of a claim or an issue in the judgment, under its title. */
export const JudgedRow = ({ issueId }: { issueId: string }) => {
  const { t } = useTranslation(undefined, { keyPrefix: "case.judged" });
  const c = useContext(JudgedCaseContext);
  const decided = useJudgedRows()?.get(issueId);
  if (decided === undefined) return null;
  const results = Object.entries(decided);
  const name = (id: string) =>
    c?.parties?.find((party) => party.id === id)?.name ?? id;
  const word = (upheld: boolean) => t(upheld ? "upheld" : "dismissed");
  return (
    <Typography
      variant="caption"
      component="p"
      sx={{ fontWeight: 600, color: "text.primary" }}
    >
      {t("result", {
        result:
          results.length === 1
            ? word(results[0][1])
            : results
                .map(([id, upheld]) => `${word(upheld)} v ${name(id)}`)
                .join(", "),
      })}
    </Typography>
  );
};
